// The tooth grammar, client side.
//
// The SERVER owns validation (the dental microservice's app/domain/teeth.py).
// This module is for LAYOUT and LABELLING — what to draw, where, and what to
// call it. It deliberately does not re-implement the rules: the chart read
// already tells us `surfacesAvailable` per tooth, and this file only needs the
// same list to render a tooth the server has never heard of yet (an unchartd
// one) before the first read lands.
//
// FDI / ISO 3950 — decided 2026-08-02, CORE.md §6.
//
//   1 upper-right   2 upper-left        5 upper-right   6 upper-left   (primary)
//   4 lower-right   3 lower-left        8 lower-right   7 lower-left
//
// THE ANATOMICAL FRAME — CORE.md §9, a patient-safety invariant, not styling:
//
//   The chart is drawn in the PATIENT's frame, as the dentist looks into the
//   mouth. Quadrants 1 and 4 (the patient's RIGHT) sit on the SCREEN's LEFT,
//   in BOTH locales. The app is RTL-first, so a well-meaning RTL pass is
//   exactly what would mirror this and put the dentist on the wrong side of
//   the mouth. The SVG is therefore rendered with an explicit `direction: ltr`
//   and absolute coordinates — never logical properties, never `--dir-flip`.

// ── The teeth, in DRAWING order (screen left → right) ───────────────────────
// Upper right runs 18→11 so the arch reads outward-to-midline on the left half,
// then 21→28 outward again on the right. That is the order a printed chart uses
// and the order arrow keys should traverse.

export const UPPER_RIGHT = ['18', '17', '16', '15', '14', '13', '12', '11'];
export const UPPER_LEFT = ['21', '22', '23', '24', '25', '26', '27', '28'];
export const LOWER_RIGHT = ['48', '47', '46', '45', '44', '43', '42', '41'];
export const LOWER_LEFT = ['31', '32', '33', '34', '35', '36', '37', '38'];

export const UPPER_RIGHT_PRIMARY = ['55', '54', '53', '52', '51'];
export const UPPER_LEFT_PRIMARY = ['61', '62', '63', '64', '65'];
export const LOWER_RIGHT_PRIMARY = ['85', '84', '83', '82', '81'];
export const LOWER_LEFT_PRIMARY = ['71', '72', '73', '74', '75'];

export const PERMANENT_ROWS = {
  upper: [...UPPER_RIGHT, ...UPPER_LEFT],
  lower: [...LOWER_RIGHT, ...LOWER_LEFT],
};

export const PRIMARY_ROWS = {
  upper: [...UPPER_RIGHT_PRIMARY, ...UPPER_LEFT_PRIMARY],
  lower: [...LOWER_RIGHT_PRIMARY, ...LOWER_LEFT_PRIMARY],
};

export const ALL_PERMANENT = [...PERMANENT_ROWS.upper, ...PERMANENT_ROWS.lower];
export const ALL_PRIMARY = [...PRIMARY_ROWS.upper, ...PRIMARY_ROWS.lower];

/**
 * The four quadrants in SCREEN order — patient's right first, upper before
 * lower — each with the primary teeth that nest inside it.
 *
 * SCREEN order, not reading order, and not FDI order: quadrants 1 and 4 are on
 * the screen's LEFT in both locales (CORE.md §9), so a picker that offered them
 * in numeric order (1,2,3,4) would run upper-right, upper-left, LOWER-LEFT,
 * lower-right — a zig-zag across the mouth. The chart draws Q1|Q2 on top and
 * Q4|Q3 beneath, and any control that stands in for the chart has to agree with
 * it, or the dentist's mental map of the arch breaks between the two.
 *
 * `teeth` is in DRAWING order within the quadrant, the same order the chart
 * lays them out and the arrow keys traverse.
 */
export const QUADRANTS = [
  { id: '1', key: 'chart.quadrant.upperRight', teeth: UPPER_RIGHT, primary: UPPER_RIGHT_PRIMARY },
  { id: '2', key: 'chart.quadrant.upperLeft', teeth: UPPER_LEFT, primary: UPPER_LEFT_PRIMARY },
  { id: '4', key: 'chart.quadrant.lowerRight', teeth: LOWER_RIGHT, primary: LOWER_RIGHT_PRIMARY },
  { id: '3', key: 'chart.quadrant.lowerLeft', teeth: LOWER_LEFT, primary: LOWER_LEFT_PRIMARY },
];

/** Has anything been charted on a primary tooth? Drives the 'auto' setting. */
export const anyPrimaryCharted = (teeth) =>
  (teeth || []).some((s) => !s.isPermanent && s.isCharted);

/**
 * Are the primary arches shown? `true` forces them on, `'auto'` shows them once
 * a primary tooth has actually been charted (mixed dentition is not derivable
 * from age — see the trap table in CORE.md §7).
 *
 * ONE rule, because the chart and the mobile tooth picker must offer the same
 * teeth: a picker listing 55–51 over a chart that is not drawing them, or the
 * reverse, is two controls describing different mouths.
 */
export const resolveWithPrimary = (showPrimary, teeth) =>
  showPrimary === true || (showPrimary === 'auto' && anyPrimaryCharted(teeth));

export const isPermanent = (fdi) => ['1', '2', '3', '4'].includes(String(fdi)[0]);
export const isUpper = (fdi) => ['1', '2', '5', '6'].includes(String(fdi)[0]);
export const position = (fdi) => Number(String(fdi)[1]);
/** Incisors and canines. Decides incisal vs occlusal — a data rule, not cosmetic. */
export const isAnterior = (fdi) => position(fdi) <= 3;

// ── Surfaces ───────────────────────────────────────────────────────────────
// M/D/F/L are universal; the fifth is incisal on anteriors and occlusal on
// posteriors. Offering the wrong one is a data error a dentist would never
// make by hand, and the server rejects it — so the UI must never present it.

export const SURFACES = { M: 'M', D: 'D', F: 'F', L: 'L', O: 'O', I: 'I' };

export const surfacesFor = (fdi) => ['M', 'D', 'F', 'L', isAnterior(fdi) ? 'I' : 'O'];

/**
 * Which way is MESIAL on screen for this tooth.
 *
 * Mesial means "toward the midline", so for a tooth on the screen's LEFT half
 * (quadrants 1 and 4, the patient's right) mesial points RIGHT, and vice versa.
 * Painting a fixed left-is-mesial box would mislabel exactly half the mouth.
 */
export const mesialIsScreenRight = (fdi) => ['1', '4', '5', '8'].includes(String(fdi)[0]);

// ── Vocabularies (must match the server's app/domain/teeth.py) ──────────────

export const PRESENCE = {
  PRESENT: 'present',
  EXTRACTED: 'extracted',
  EXFOLIATED: 'exfoliated',
  CONGENITALLY_ABSENT: 'congenitally_absent',
  UNERUPTED: 'unerupted',
  IMPACTED: 'impacted',
};

/** A tooth that is not in the mouth. Drawn as a ghost, never editable per surface. */
export const isMissing = (presence) => [
  PRESENCE.EXTRACTED, PRESENCE.EXFOLIATED, PRESENCE.CONGENITALLY_ABSENT,
].includes(presence);

export const PRESENCE_OPTIONS = [
  PRESENCE.PRESENT, PRESENCE.EXTRACTED, PRESENCE.EXFOLIATED,
  PRESENCE.CONGENITALLY_ABSENT, PRESENCE.UNERUPTED, PRESENCE.IMPACTED,
];

export const CONDITION = {
  SOUND: 'sound',
  CARIES: 'caries',
  ROOT_CANAL: 'root_canal',
  POST_CORE: 'post_core',
  CROWN: 'crown',
  VENEER: 'veneer',
  IMPLANT: 'implant',
  BRIDGE_ABUTMENT: 'bridge_abutment',
  BRIDGE_PONTIC: 'bridge_pontic',
  FRACTURE: 'fracture',
  ATTRITION: 'attrition',
  MOBILITY: 'mobility',
  PERIAPICAL_LESION: 'periapical_lesion',
  SEALANT: 'sealant',
  TEMPORARY: 'temporary',
  WATCH: 'watch',
};

// Ordered for the picker: what a dentist reaches for most, first.
export const CONDITION_OPTIONS = [
  CONDITION.CARIES, CONDITION.ROOT_CANAL, CONDITION.CROWN, CONDITION.IMPLANT,
  CONDITION.VENEER, CONDITION.POST_CORE, CONDITION.BRIDGE_ABUTMENT,
  CONDITION.BRIDGE_PONTIC, CONDITION.SEALANT, CONDITION.TEMPORARY,
  CONDITION.FRACTURE, CONDITION.ATTRITION, CONDITION.MOBILITY,
  CONDITION.PERIAPICAL_LESION, CONDITION.WATCH, CONDITION.SOUND,
];

export const SURFACE_STATE = {
  SOUND: 'sound',
  CARIES: 'caries',
  RESTORATION: 'restoration',
  RECURRENT_CARIES: 'recurrent_caries',
  TEMPORARY: 'temporary',
  SEALANT: 'sealant',
  FRACTURED: 'fractured',
};

export const SURFACE_STATE_OPTIONS = [
  SURFACE_STATE.CARIES, SURFACE_STATE.RESTORATION, SURFACE_STATE.RECURRENT_CARIES,
  SURFACE_STATE.TEMPORARY, SURFACE_STATE.SEALANT, SURFACE_STATE.FRACTURED,
  SURFACE_STATE.SOUND,
];

export const MATERIALS = [
  'composite', 'amalgam', 'glass_ionomer', 'zirconia', 'pfm',
  'full_metal', 'emax', 'temporary', 'gold',
];

/** Materials worth offering for a given state. A presence has none; a crown is
 *  not made of glass ionomer. Narrowing the list is what keeps the picker
 *  usable rather than a wall of 9 options on every field. */
export function materialsFor(aspect, value) {
  if (aspect === 'presence') return [];
  if (value === CONDITION.CROWN || value === CONDITION.BRIDGE_ABUTMENT
      || value === CONDITION.BRIDGE_PONTIC) {
    return ['zirconia', 'pfm', 'full_metal', 'emax', 'gold', 'temporary'];
  }
  if (value === CONDITION.VENEER) return ['emax', 'composite', 'zirconia'];
  if (value === SURFACE_STATE.RESTORATION) {
    return ['composite', 'amalgam', 'glass_ionomer', 'gold', 'temporary'];
  }
  return [];
}

// ── Colour semantics ───────────────────────────────────────────────────────
// A chart is read at a glance, so the palette carries meaning:
//   red    = needs treatment (decay, fracture)
//   blue   = existing restoration / prosthetic work
//   grey   = not in the mouth
//   amber  = provisional or watch
// Deliberately NOT taken from statusColors: that vocabulary is appointment
// states (نوبت‌دار/حاضر), and reusing it here would tie two unrelated meanings
// to one palette so a change to either would silently move the other.

export const TOOTH_COLORS = {
  sound: { fill: '#ffffff', stroke: '#94a3b8' },
  caries: { fill: '#ef4444', stroke: '#b91c1c' },
  recurrent_caries: { fill: '#f97316', stroke: '#c2410c' },
  restoration: { fill: '#3b82f6', stroke: '#1d4ed8' },
  temporary: { fill: '#f59e0b', stroke: '#b45309' },
  sealant: { fill: '#22c55e', stroke: '#15803d' },
  fractured: { fill: '#a855f7', stroke: '#7e22ce' },
  missing: { fill: '#e2e8f0', stroke: '#cbd5e1' },
  untouched: { fill: '#f8fafc', stroke: '#cbd5e1' },
  // Whole-tooth condition colours — the glyph layer and `toothOutline` share
  // these, so the outline tint and the glyph can never disagree. Their fills are
  // PALE on purpose: they wash a whole crown (and back a whole picker chip),
  // where the saturated surface colours above would drown everything drawn over
  // them and leave dark label text unreadable.
  //
  // `caries_tooth` is decay recorded against the WHOLE tooth rather than a
  // surface. It needs its own entry precisely because it must not be the
  // surface red: the saturated red goes on painting ONE facet, and this pale
  // field says the whole crown — two readings the chart has to keep apart.
  caries_tooth: { fill: '#fecdd3', stroke: '#b91c1c' },
  implant: { fill: '#e0e7ff', stroke: '#4f46e5' },
  root_canal: { fill: '#fce7f3', stroke: '#be185d' },
  post_core: { fill: '#dbeafe', stroke: '#1d4ed8' },
  bridge: { fill: '#bfdbfe', stroke: '#3b82f6' },
};

// Decorative shades for the chart's depth pass — gradient stops, the arch
// shadow, the hovered-surface stroke. Named HERE, not inlined in components,
// so the whole chart palette stays in one file. None of these carries clinical
// meaning; that is what TOOTH_COLORS is for.
export const CHART_SHADES = {
  crownCentre: '#ffffff',
  crownRim: '#e8edf3',
  archShadow: '#94a3b8',
  surfaceHover: '#0EA5A0',
  // "Something is recorded here that this chart has no mark for" — see
  // `hasUndrawnCondition`. Deliberately neutral: it says a finding EXISTS and
  // refuses to say what, because inventing a symbol for لقی or سایش is a
  // clinical convention to settle with a dentist, not in a renderer.
  noteMark: '#475569',
  // The PNG export (exportChart.js): an opaque page behind the chart —
  // referrals get printed, and a transparent PNG on a dark viewer is
  // unreadable — and the patient-context header line above it.
  exportBackground: '#ffffff',
  exportHeader: '#334155',
};

/** The بریج bar's stroke recipe — ONE definition shared by the chart
 *  (Odontogram.jsx) and its legend swatch (ChartTab.jsx), like the glyph
 *  generators are, so the legend can never drift from what the chart draws.
 *  Keys are React SVG props on purpose: both call sites spread it. */
export const BRIDGE_BAR = {
  stroke: TOOTH_COLORS.bridge.stroke,
  strokeWidth: 7,
  strokeOpacity: 0.35,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

/** ── The two ANNOTATION recipes ───────────────────────────────────────────
 *
 *  Neither of these is a tooth STATE. They are a caller's statement ABOUT a
 *  tooth — «something is planned here», «something was done here» — which is
 *  why they live beside the state palette rather than in it, and why nothing
 *  reads them off a `state` object.
 *
 *  Like BRIDGE_BAR: ONE definition, spread by the chart (Tooth.jsx) and by
 *  both legends (ChartTab.jsx, MobileChartLegend.jsx), so a key can never
 *  drift from the mark it is decoding. Keys are React SVG props.
 */
export const TREATED_BADGE = {
  // Teal — Parsina's own accent, and the one colour on this chart that belongs
  // to the APP rather than to the mouth. Nothing clinical is teal, so the badge
  // cannot be mistaken for a finding.
  fill: '#0d9488',
  ink: '#ffffff',
  r: 3.1,
};

export const PLANNED_RING = {
  // Amber — the palette's "provisional or watch" (§ the colour language
  // above): planned work is exactly that, a thing intended and not yet true.
  stroke: '#f59e0b',
  strokeWidth: 2.5,
  // Dashed, and that is the load-bearing part: every SOLID outline on this
  // chart is a fact about the tooth. A dashed one cannot be misread as one.
  strokeDasharray: '4 3',
  strokeOpacity: 0.9,
};

/** روکش ring tint by material. The ring SHAPE says "crown"; the tint says
 *  what it is made of — colour reinforces, never carries alone. */
export const CROWN_MATERIAL_TINTS = {
  gold: '#b45309',
  zirconia: '#64748b',
  emax: '#64748b',
  full_metal: '#64748b',
  pfm: '#475569',
};

export const crownRingTint = (material) =>
  CROWN_MATERIAL_TINTS[material] || TOOTH_COLORS.restoration.stroke;

export const surfaceColor = (value) => TOOTH_COLORS[value] || TOOTH_COLORS.sound;

/**
 * Which whole-tooth condition tints the tooth, in precedence order.
 *
 * CARIES LEADS. The palette's own rule is «red = needs treatment», and a
 * crowned or root-treated tooth that is ALSO carious is not finished work — the
 * chart has to say the untreated thing first. It was absent from this list
 * entirely, which is why «پوسیدگی در کل دندان هجدهم» drew a tooth
 * indistinguishable from a sound one: no tint, and no glyph either.
 *
 * One ordered list, so the tint (`toothOutline`) and everything that keys off
 * "is this tooth tinted" cannot fall out of step.
 */
export const OUTLINE_CONDITIONS = [
  [CONDITION.CARIES, 'caries_tooth'],
  [CONDITION.IMPLANT, 'implant'],
  [CONDITION.CROWN, 'restoration'],
  [CONDITION.ROOT_CANAL, 'root_canal'],
];

/** The condition tinting this tooth, or null. Null for a missing tooth: a ghost
 *  is grey whatever its history. */
export function tintingCondition(state) {
  if (!state || isMissing(state.presence)) return null;
  const values = (state.conditions || []).map((c) => c.value);
  const hit = OUTLINE_CONDITIONS.find(([value]) => values.includes(value));
  return hit ? hit[0] : null;
}

/** The outline colour for the whole tooth, from its conditions. */
export function toothOutline(state) {
  if (!state) return TOOTH_COLORS.untouched;
  if (isMissing(state.presence)) return TOOTH_COLORS.missing;
  const tinting = tintingCondition(state);
  if (tinting) {
    return TOOTH_COLORS[OUTLINE_CONDITIONS.find(([value]) => value === tinting)[1]];
  }
  if (!state.isCharted) return TOOTH_COLORS.untouched;
  return TOOTH_COLORS.sound;
}

/**
 * Whole-tooth conditions the arch draws SOMETHING for — a tint, a glyph, or a
 * bar along the span.
 *
 * Everything outside this list — سایش, لقی, ضایعهٔ اپیکال, فیشورسیلانت, موقت,
 * تحت نظر — is recorded on the tooth and drawn nowhere, so a dentist who
 * dictates one sees the chart not move. `hasUndrawnCondition` is what lets the
 * tooth admit it is carrying something rather than look untouched; the hover
 * card names it.
 */
export const DRAWN_CONDITIONS = [
  CONDITION.CARIES, CONDITION.IMPLANT, CONDITION.CROWN, CONDITION.ROOT_CANAL,
  CONDITION.POST_CORE, CONDITION.VENEER, CONDITION.FRACTURE,
  CONDITION.BRIDGE_ABUTMENT, CONDITION.BRIDGE_PONTIC,
];

export const hasUndrawnCondition = (state) =>
  (state?.conditions || []).some(
    (c) => c.value !== CONDITION.SOUND && !DRAWN_CONDITIONS.includes(c.value),
  );

// ── Bridges ────────────────────────────────────────────────────────────────

/**
 * The bridge spans of ONE row, for the bar the chart draws along the arch.
 *
 * A span is a maximal run of ADJACENT teeth — adjacent in DRAWING order, the
 * order `rowOrder` lists them — whose conditions include `bridge_abutment` or
 * `bridge_pontic`, containing at least one abutment. A lone pontic with no
 * abutment is charting noise, not a bridge, and yields nothing.
 *
 * Presence is deliberately ignored: a pontic tooth is usually EXTRACTED — the
 * bridge exists precisely because the tooth does not — so the span must carry
 * ghost teeth exactly like present ones.
 *
 * Upper and lower are SEPARATE rowOrders and must be passed separately: this
 * function only ever walks one row, so a span can never leak across the
 * midline gap between the arches.
 *
 * @param teethStates map of fdi → tooth state (the odontogram's `states`)
 * @param rowOrder    one row's FDIs in drawing order (e.g. PERMANENT_ROWS.lower)
 * @returns [{ teeth: [fdi...], row }] — `row` echoes `rowOrder` so the
 *          renderer knows which arch to place the bar on
 */
export function bridgeSpans(teethStates, rowOrder) {
  const spans = [];
  let run = null;
  const flush = () => {
    if (run && run.hasAbutment) spans.push({ teeth: run.teeth, row: rowOrder });
    run = null;
  };
  for (const fdi of rowOrder) {
    const values = ((teethStates[fdi] || {}).conditions || []).map((c) => c.value);
    const onBridge = values.includes(CONDITION.BRIDGE_ABUTMENT)
      || values.includes(CONDITION.BRIDGE_PONTIC);
    if (!onBridge) { flush(); continue; }
    if (!run) run = { teeth: [], hasAbutment: false };
    run.teeth.push(fdi);
    if (values.includes(CONDITION.BRIDGE_ABUTMENT)) run.hasAbutment = true;
  }
  flush();
  return spans;
}

// ── Keyboard traversal ─────────────────────────────────────────────────────
//
// The chart is ONE tab stop; arrow keys move a cursor inside it. These are
// pure helpers so the traversal order is testable without a DOM.
//
// Traversal follows DRAWING order and deliberately IGNORES RTL (CORE.md §9):
// ArrowRight always moves toward the SCREEN's right — the next tooth the
// chart draws — in both locales. The arch is anatomical, not textual;
// flipping the arrows under fa would walk the mouth against the drawn chart.

/** The rows the arrows walk, in VERTICAL screen order (ArrowDown reads
 *  downward). Primary arches nest BETWEEN the permanent ones because that is
 *  where they sit on screen in a mixed dentition. */
export function visibleRows(withPrimary) {
  return withPrimary
    ? [PERMANENT_ROWS.upper, PRIMARY_ROWS.upper, PRIMARY_ROWS.lower, PERMANENT_ROWS.lower]
    : [PERMANENT_ROWS.upper, PERMANENT_ROWS.lower];
}

/**
 * The next cursor position for an arrow key.
 *
 * @param rows  visibleRows(...) — arrays of FDIs in drawing order, top-down
 * @param fdi   current cursor, or null (the first arrow enters at the
 *              top-left — the first tooth the chart draws)
 * @param key   'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown'
 */
export function moveCursor(rows, fdi, key) {
  let r = -1;
  let i = -1;
  rows.forEach((row, ri) => {
    const ii = row.indexOf(fdi);
    if (ii !== -1) { r = ri; i = ii; }
  });
  // No cursor yet — or the cursor's row was just hidden (primary toggled off).
  if (r === -1) return rows[0][0];

  if (key === 'ArrowRight') return rows[r][Math.min(i + 1, rows[r].length - 1)];
  if (key === 'ArrowLeft') return rows[r][Math.max(i - 1, 0)];

  if (key === 'ArrowUp' || key === 'ArrowDown') {
    const nr = r + (key === 'ArrowDown' ? 1 : -1);
    if (nr < 0 || nr >= rows.length) return fdi;   // clamp at the jaws' edge
    const row = rows[nr];
    // "Nearest index", generalized proportionally to rows of different
    // lengths (16 permanent vs 10 primary), which tracks screen position
    // closely enough; for the equal-length permanent rows it is the SAME
    // index — 16 lands on 46, the tooth it bites against.
    const ni = Math.round((i * (row.length - 1)) / Math.max(rows[r].length - 1, 1));
    return row[ni];
  }
  return fdi;
}

// ── The FDI jump buffer ────────────────────────────────────────────────────
//
// Typing two digits while the chart is focused jumps the cursor to that FDI
// («3», «6» → دندان 36). A pure reducer so the timing rule is testable
// without a DOM: the caller passes `now` in and applies `jump` out.

export const FDI_BUFFER_MS = 800;
export const FDI_JUMP_IDLE = { buffer: '', at: 0, jump: null };

/** '0'–'9' from an ASCII, Persian (۰–۹) or Arabic-Indic (٠–٩) digit key, or
 *  null. A fa keyboard layout emits U+06F0–06F9 for the number row, and a
 *  dentist on it must be able to jump teeth as fluently as one on EN. */
export function asciiDigit(key) {
  if (typeof key !== 'string' || key.length !== 1) return null;
  if (key >= '0' && key <= '9') return key;
  const cp = key.codePointAt(0);
  if (cp >= 0x06f0 && cp <= 0x06f9) return String(cp - 0x06f0);   // ۰-۹
  if (cp >= 0x0660 && cp <= 0x0669) return String(cp - 0x0660);   // ٠-٩
  return null;
}

/**
 * fdiJumpReducer(state, action) → { buffer, at, jump }
 *
 * actions:
 *   { type: 'digit', digit, now, teeth }  a digit key pressed at `now` (ms);
 *                                         `teeth` = the FDIs currently SHOWN
 *   { type: 'clear' }                     Escape
 *
 * `jump` is an instruction to the caller — the FDI to move the cursor to —
 * non-null only on the transition that completed a valid pair. A completed
 * pair naming no visible tooth is swallowed WHOLE ('99' then '36' reads as
 * two attempts, not '9' + '93' + …), and a first digit older than
 * FDI_BUFFER_MS is stale: the new digit starts a fresh buffer.
 */
export function fdiJumpReducer(state, action) {
  const s = state || FDI_JUMP_IDLE;
  if (action.type === 'clear') return FDI_JUMP_IDLE;
  if (action.type !== 'digit') return s.jump ? { ...s, jump: null } : s;
  const digit = asciiDigit(action.digit);
  if (digit == null) return s.jump ? { ...s, jump: null } : s;
  const stale = action.now - s.at > FDI_BUFFER_MS;
  const buffer = (stale ? '' : s.buffer) + digit;
  if (buffer.length < 2) return { buffer, at: action.now, jump: null };
  return {
    buffer: '',
    at: action.now,
    jump: (action.teeth || []).includes(buffer) ? buffer : null,
  };
}

// ── Anatomical names ───────────────────────────────────────────────────────
//
// The name a dentist would say out loud. Position (the second FDI digit) alone
// determines it — with ONE trap:
//
//   PERMANENT positions 4 and 5 are PREMOLARS.
//   PRIMARY   positions 4 and 5 are MOLARS — a child has no premolars at all;
//             the permanent premolars erupt into those positions later.
//
// So `54` is "second primary molar", never "first premolar". Getting this
// wrong would put the wrong tooth name on a child's chart.

const PERMANENT_NAME_BY_POSITION = {
  1: 'centralIncisor', 2: 'lateralIncisor', 3: 'canine',
  4: 'firstPremolar', 5: 'secondPremolar',
  6: 'firstMolar', 7: 'secondMolar', 8: 'thirdMolar',
};

const PRIMARY_NAME_BY_POSITION = {
  1: 'centralIncisor', 2: 'lateralIncisor', 3: 'canine',
  4: 'firstMolar', 5: 'secondMolar',
};

/** i18n key for this tooth's anatomical name, e.g. `chart.toothName.firstMolar`. */
export function toothNameKey(fdi) {
  const table = isPermanent(fdi) ? PERMANENT_NAME_BY_POSITION : PRIMARY_NAME_BY_POSITION;
  const name = table[position(fdi)];
  return name ? `chart.toothName.${name}` : '';
}

/** i18n key for the quadrant, as a dentist names it: upper/lower × right/left,
 *  in the PATIENT's frame (which is what the chart is drawn in). */
export function quadrantKey(fdi) {
  const q = String(fdi)[0];
  const upper = isUpper(fdi);
  const right = ['1', '4', '5', '8'].includes(q);
  return `chart.quadrant.${upper ? 'upper' : 'lower'}${right ? 'Right' : 'Left'}`;
}

/** Everything the hover card needs, derived in ONE place so the tooltip and any
 *  later surface (a print view, a referral letter) cannot disagree. */
export function toothSummary(fdi, state) {
  const surfaces = (state?.surfaces || []).filter((s) => s.value && s.value !== 'sound');
  return {
    fdi,
    universal: state?.universal || '',
    nameKey: toothNameKey(fdi),
    quadrantKey: quadrantKey(fdi),
    isPrimary: !isPermanent(fdi),
    presence: state?.presence || PRESENCE.PRESENT,
    isMissingTooth: isMissing(state?.presence),
    // A surface recorded as SOUND is a finding, but it is noise in a hover
    // card — the card answers "what is wrong with this tooth".
    surfaces,
    conditions: (state?.conditions || []).filter((c) => c.value && c.value !== 'sound'),
    notes: state?.notes || [],
    isCharted: !!state?.isCharted,
    lastRecordedAtMs: state?.lastRecordedAtMs || null,
  };
}
